import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

import { bucketFor, ruleForBucket } from './scoreScale';

interface Props {
  /**
   * A whole score, an average (rendered to one decimal), or `null` for no score at all — which is
   * an em dash, never a `0`. A zero would enter the reader's arithmetic; an em dash does not.
   */
  value: number | null | undefined;
  className?: string;
}

/**
 * Scores are the densest information in the product, so the colour lives in a **rule beneath the
 * numeral** rather than in a filled chip: chips in a table column produce a wall of colour nobody
 * can read, and they editorialise the reader's impression before they have read the comment.
 *
 * The numeral is always ink. 3 is neutral grey, not amber — it is the expected outcome, not a
 * warning. The label goes to assistive technology, because the rule alone carries meaning that a
 * screen reader cannot see (§9).
 */
export function Score({ value, className }: Props) {
  const { t } = useTranslation('components');

  if (value === null || value === undefined) {
    return (
      <span
        className={cn(type.numeral, 'text-ink-500', className)}
        role="img"
        aria-label={t('score.none')}
      >
        —
      </span>
    );
  }

  const isAverage = !Number.isInteger(value);
  const bucket = bucketFor(value);
  const display = isAverage ? value.toFixed(1) : String(value);

  return (
    <span
      className={cn('inline-flex flex-col items-center leading-none', className)}
      role="img"
      aria-label={t('score.announce', { value: display, label: t(`score.${bucket}`) })}
    >
      <span className={cn(type.numeral, 'text-ink-900')}>{display}</span>
      <span aria-hidden className={cn('mt-1 w-full', ruleForBucket[bucket])} />
    </span>
  );
}
